import { Link } from '@tanstack/react-router';
import { useId, useMemo, useRef, useState, type ReactNode } from 'react';

import { Botao, BotaoDestrutivo } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo } from '@/componentes/Campo';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Medida } from '@/componentes/Medida';
import { DadoChave, PainelDetalhe } from '@/componentes/PainelDetalhe';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { carregandoA } from '@/componentes/carregando';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { formatarData, formatarDataHora, formatarDecimal, formatarInteiro } from '@/lib/formato';
import { useSessao } from '@/modulos/sessao/consultas';

import type { LinhaImportadaDaApi, LoteDaApi } from './api';
import { SITUACOES_DO_LOTE, TAMANHO_MAXIMO_DO_ARQUIVO, decodificarArquivo } from './conciliacao';
import { explicarFalha, useDescartar, useImportacao, useImportacoes, useImportar, usePromover } from './consultas';
import { usePaginacao } from './paginacao';
import { contar, fraseDoErro } from './texto';

type Recusa = { readonly erro: string; readonly comoResolver: string };

/** O que promover ou descartar um lote respondeu. Mora na página: o painel se refaz quando o lote muda de situação. */
interface ResultadoDoLote {
  readonly loteId: string;
  readonly aviso: string | null;
  readonly recusa: Recusa | null;
}

/**
 * OPE 03 — a área de estágio das horas reconhecidas.
 *
 * O arquivo entra, é lido linha a linha e FICA AQUI: nada vira hora
 * reconhecida até alguém conferir e promover. As linhas com erro aparecem com
 * a frase do que corrigir, e as boas não esperam pelas ruins — mas promover
 * leva só as boas, e o painel diz quantas ficaram de fora.
 *
 * O formato é a planilha padrão (equipamento; dia; horas) enquanto o formato
 * do cliente não chega — pergunta #12 do placar.
 */
const diaDe = (dia: string | null) => (dia === null ? '—' : formatarData(new Date(`${dia}T12:00:00`)));

function periodo(l: LoteDaApi): string {
  if (!l.periodoInicio || !l.periodoFim) return 'sem linha válida';
  return l.periodoInicio === l.periodoFim ? diaDe(l.periodoInicio) : `${diaDe(l.periodoInicio)} a ${diaDe(l.periodoFim)}`;
}

const COLUNAS: readonly Coluna<LoteDaApi>[] = [
  {
    chave: 'arquivo',
    rotulo: 'Arquivo',
    /* O nome do arquivo não alarga a tabela para fora do quadro: a coluna pede
       o que ele precisa e, quando o quadro aperta (painel aberto), o nome
       desce a duas linhas, quebrando dentro da palavra se preciso. Inteiro,
       ele está no `title`, no cartão e no título do painel. */
    celula: (l) => (
      <span className="flex flex-col">
        <TextoDaCelula texto={l.nomeArquivo} classe="txt-corpo text-tinta" />
        <TextoDaCelula
          texto={`${formatarDataHora(new Date(l.criadoEm))} · ${l.criadoPor ?? '—'}`}
          classe="txt-apoio text-tinta-secundaria"
        />
      </span>
    ),
  },
  {
    chave: 'periodo',
    rotulo: 'Período',
    /* Secundária (R01, p.28): o período está no subtítulo do painel do lote. */
    prioridade: 'secundaria',
    celula: (l) => <span className="txt-corpo text-tinta">{periodo(l)}</span>,
  },
  {
    chave: 'validas',
    rotulo: 'Linhas válidas',
    alinhamento: 'numero',
    celula: (l) => <Medida valor={formatarInteiro(l.linhasValidas)} unidade={`de ${formatarInteiro(l.linhas)}`} />,
  },
  {
    chave: 'situacao',
    rotulo: 'Situação',
    alinhamento: 'status',
    celula: (l) => <span className="txt-corpo text-tinta">{SITUACOES_DO_LOTE[l.situacao] ?? l.situacao}</span>,
  },
];

/**
 * As linhas do arquivo, no painel do lote: LISTA, no desenho dos cartões da
 * tabela (R01, p.28), e não tabela de três colunas.
 *
 * Numa tabela, a célula não quebra — e a frase de correção, que é a razão de
 * existir da aba "Com erro", era cortada no meio da palavra num quadro de 360
 * px ("Corrij|"), com a coluna das horas fora da vista. Aqui a frase quebra
 * normalmente e é lida inteira (p.23, regra 10: o erro diz o que fazer, e
 * precisa poder ser lido); a linha bruta do arquivo vem embaixo, em
 * monoespaçada, quebrando em qualquer ponto; as horas da linha válida, à
 * direita, com a unidade em cinza (p.12).
 */
function ListaDeLinhas({
  rotulo,
  linhas,
  carregando,
  erro,
  vazio,
}: {
  readonly rotulo: string;
  readonly linhas: readonly LinhaImportadaDaApi[];
  readonly carregando: boolean;
  readonly erro: ReactNode;
  readonly vazio: ReactNode;
}) {
  /* Carregando (p.32) na forma desta lista: "Linha N", o conteúdo em duas
     linhas e o número à direita — nunca frase ou rodinha. */
  if (carregando) {
    return (
      <Carregando oQue={carregandoA(rotulo)} className="flex flex-col">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 border-t border-borda-sutil py-3 first:border-t-0 first:pt-0"
          >
            <Barra className="h-3 w-16" />
            <span className="flex min-w-0 flex-col gap-2">
              <Barra className={`h-3 ${i % 2 === 0 ? 'w-1/2' : 'w-2/5'}`} />
              <Barra className="h-3 w-3/4" />
            </span>
            <Barra className="h-3 w-12" />
          </div>
        ))}
      </Carregando>
    );
  }
  if (erro) return erro;
  if (linhas.length === 0) return vazio;
  return (
    <ul aria-label={rotulo} className="flex flex-col">
      {linhas.map((l) => (
        <li
          key={l.numeroLinha}
          className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-3 gap-y-1 border-t border-borda-sutil py-3 first:border-t-0 first:pt-0"
        >
          <span className="txt-apoio whitespace-nowrap tabular-nums text-tinta-secundaria">
            {`Linha ${formatarInteiro(l.numeroLinha)}`}
          </span>
          {l.erro === null ? (
            <>
              <span className="flex min-w-0 flex-col">
                {/* Código em monoespaçada (p.18). */}
                <span className="codigo text-tinta">{l.equipamento ?? l.codigoEquipamento}</span>
                <span className="txt-apoio text-tinta-secundaria">{diaDe(l.dia)}</span>
              </span>
              {l.horas === null ? (
                <span className="txt-apoio text-tinta-secundaria">—</span>
              ) : (
                <Medida valor={formatarDecimal(l.horas, 2)} unidade="h" />
              )}
            </>
          ) : (
            <span className="col-span-2 flex min-w-0 flex-col gap-1">
              <span className="txt-corpo text-pretty text-tinta">{l.erro}</span>
              <span className="mono txt-apoio wrap-anywhere text-tinta-secundaria">{l.bruto}</span>
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

export function PaginaImportacoes() {
  const base = useId();
  const arquivo = useRef<HTMLInputElement>(null);
  const lotes = useImportacoes();
  const importar = useImportar();
  /* Importar, promover e descartar são de quem confere apontamento (prova do
     marco, 07/10/2026): quem só lê a operação vê os lotes, sem os botões. */
  const sessao = useSessao();
  const podeConferir = (sessao.data?.permissoes ?? []).includes('apontamento.conferir');
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [recusa, setRecusa] = useState<Recusa | null>(null);
  const [resultado, setResultado] = useState<ResultadoDoLote | null>(null);

  const linhas = useMemo(() => lotes.data?.lotes ?? [], [lotes.data]);
  const aberto = linhas.find((l) => l.id === selecionado) ?? null;
  const { visiveis, rodape } = usePaginacao(linhas);

  function abrirLote(id: string | null) {
    setSelecionado(id);
    /* O resultado é do lote em que ele aconteceu, e não segue para o próximo. */
    setResultado((atual) => (atual !== null && atual.loteId === id ? atual : null));
  }

  async function escolher(evento: React.ChangeEvent<HTMLInputElement>) {
    const escolhido = evento.target.files?.[0];
    evento.target.value = '';
    if (!escolhido) return;
    setRecusa(null);
    if (escolhido.size > TAMANHO_MAXIMO_DO_ARQUIVO) {
      setRecusa({
        erro: 'o arquivo é grande demais',
        comoResolver: 'O limite é 3 MB, cerca de 5.000 linhas. Divida o período em mais de um arquivo.',
      });
      return;
    }
    const conteudo = decodificarArquivo(await escolhido.arrayBuffer());
    importar.mutate(
      { nomeArquivo: escolhido.name, conteudo },
      {
        onSuccess: (r) => {
          abrirLote(r.loteId);
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  return (
    <>
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Operação"
            titulo="Importações de horas"
            subtitulo="O arquivo do cliente passa pela conferência antes de valer"
            acoes={
              <>
                <Link to="/operacao/conciliacao" className="botao botao-secundario">
                  Ver conciliação
                </Link>
                {/* UM LARANJA POR TELA: com um lote aberto, o laranja é do
                    painel ("Promover lote"), e importar passa a secundário. */}
                {podeConferir ? (
                  <Botao
                    nivel={aberto?.situacao === 'estagio' ? 'secundario' : 'primario'}
                    disabled={importar.isPending}
                    onClick={() => arquivo.current?.click()}
                  >
                    {importar.isPending ? 'Importando…' : 'Importar arquivo'}
                  </Botao>
                ) : null}
                {podeConferir ? (
                <input
                  ref={arquivo}
                  id={`${base}-arquivo`}
                  type="file"
                  accept=".csv,.txt,text/csv,text/plain"
                  className="hidden"
                  aria-label="Arquivo de horas reconhecidas"
                  onChange={(e) => {
                    void escolher(e);
                  }}
                />
                ) : null}
              </>
            }
          />

          <section className="cartao flex flex-col gap-2 p-6">
            <h2 className="txt-secao text-tinta">O formato do arquivo</h2>
            <p className="txt-apoio max-w-[68ch] text-pretty text-tinta-secundaria">
              CSV salvo pelo Excel, com três colunas em qualquer ordem: equipamento, dia e horas. O dia em
              dd/mm/aaaa; as horas com vírgula, como 7,5. O mesmo arquivo não entra duas vezes, e um arquivo
              corrigido substitui o dia que já existia.
            </p>
            <pre className="mono txt-apoio rounded-controle bg-rebaixada p-4 text-tinta">
              {'equipamento;dia;horas\nESC-001;18/09/2026;7,5'}
            </pre>
          </section>

          {recusa === null ? null : (
            <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
              <span className="txt-corpo text-tinta">{fraseDoErro(recusa.erro)}</span>
              <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
            </div>
          )}

          <Tabela
            rotulo="Arquivos importados"
            colunas={COLUNAS}
            linhas={visiveis}
            alturaDupla
            chaveDaLinha={(l) => l.id}
            selecionada={selecionado}
            aoSelecionar={(l) => {
              abrirLote(l.id);
            }}
            carregando={lotes.isPending}
            erro={
              lotes.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar as importações"
                  descricao={explicarFalha(lotes.error).comoResolver}
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void lotes.refetch();
                      }}
                    >
                      Carregar de novo
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              /* PRIMEIRO USO COM SAÍDA (regra 9; p.23): o vazio oferece a
                 ação, pelo mesmo seletor de arquivo do cabeçalho. */
              <EstadoVazio
                titulo="Nenhum arquivo importado"
                descricao={
                  podeConferir
                    ? 'Importe o arquivo de horas reconhecidas do cliente para comparar com o apontamento e o horímetro.'
                    : 'Quem importa o arquivo de horas do cliente é o perfil Gestor operacional. Os lotes aparecem aqui depois.'
                }
                acao={
                  podeConferir ? (
                    <Botao nivel="secundario" disabled={importar.isPending} onClick={() => arquivo.current?.click()}>
                      Importar arquivo
                    </Botao>
                  ) : undefined
                }
              />
            }
            rodape={
              /* RODAPÉ DA p.18, e o aviso do teto DENTRO da margem dele: a rota
                 devolve só os lotes mais recentes. */
              <>
                <RodapeTabela {...rodape} />
                {lotes.data && lotes.data.total > linhas.length ? (
                  <p className="txt-apoio border-t border-borda-sutil bg-rebaixada px-4 py-2 text-tinta-secundaria">
                    {`São ${formatarInteiro(lotes.data.total)} arquivos importados; a lista traz os ${formatarInteiro(linhas.length)} mais recentes.`}
                  </p>
                ) : null}
              </>
            }
          />
        </div>
      </div>

      {aberto ? (
        <Detalhe
          /* A SITUAÇÃO NA CHAVE: promover ou descartar tira a aba "Descartar",
             e o painel precisa recomeçar numa aba que ainda existe. */
          key={`${aberto.id}-${aberto.situacao}`}
          lote={aberto}
          resultado={resultado !== null && resultado.loteId === aberto.id ? resultado : null}
          aoResultado={setResultado}
          aoFechar={() => {
            abrirLote(null);
          }}
        />
      ) : null}
    </>
  );
}

function Detalhe({
  lote,
  resultado,
  aoResultado,
  aoFechar,
}: {
  readonly lote: LoteDaApi;
  readonly resultado: ResultadoDoLote | null;
  readonly aoResultado: (resultado: ResultadoDoLote | null) => void;
  readonly aoFechar: () => void;
}) {
  const base = useId();
  const detalhe = useImportacao(lote.id);
  const promover = usePromover();
  const descartar = useDescartar();
  const [motivo, setMotivo] = useState('');
  const sessao = useSessao();
  const podeConferir = (sessao.data?.permissoes ?? []).includes('apontamento.conferir');

  const todas = detalhe.data?.linhas ?? [];
  const comErro = todas.filter((l) => l.erro !== null);
  const validas = todas.filter((l) => l.erro === null);
  /* Promover e descartar só para quem confere; os outros leem o lote. */
  const emEstagio = lote.situacao === 'estagio' && podeConferir;

  const avisar = (aviso: string) => {
    aoResultado({ loteId: lote.id, aviso, recusa: null });
  };
  const recusar = (recusa: Recusa) => {
    aoResultado({ loteId: lote.id, aviso: null, recusa });
  };

  function aoPromover() {
    aoResultado(null);
    promover.mutate(lote.id, {
      onSuccess: (r) => {
        avisar(
          `${contar(r.promovidas, 'dia de horas reconhecidas gravado', 'dias de horas reconhecidas gravados')}. ${
            r.divergenciasAbertas === 0
              ? 'Nenhuma divergência aberta no período.'
              : `${contar(r.divergenciasAbertas, 'divergência aberta', 'divergências abertas')} no período — veja a conciliação.`
          }`,
        );
      },
      onError: (falha) => {
        recusar(explicarFalha(falha));
      },
    });
  }

  function aoDescartar() {
    aoResultado(null);
    descartar.mutate(
      { id: lote.id, motivo: motivo.trim() },
      {
        onSuccess: () => {
          avisar('Lote descartado. O mesmo arquivo pode ser importado de novo.');
        },
        onError: (falha) => {
          recusar(explicarFalha(falha));
        },
      },
    );
  }

  /* A MENSAGEM DE RESULTADO MORA JUNTO DAS AÇÕES (p.19: identidade,
     dados-chave, abas, ações). Antes ela saía no meio dos dados-chave,
     misturada aos pares rótulo e valor. Aqui ela fica presa ao pé da aba
     (`sticky`), logo acima do rodapé de ações do painel, e vale para as abas
     todas — promover ou descartar troca as abas, e a mensagem não some. */
  const mensagem =
    resultado === null ? null : (
      <div className="sticky bottom-0 flex flex-col gap-2 bg-card pt-4">
        {resultado.aviso === null ? null : (
          <p role="status" className="txt-apoio text-pretty text-tinta">
            {resultado.aviso}
          </p>
        )}
        {resultado.recusa === null ? null : (
          <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="txt-corpo text-tinta">{fraseDoErro(resultado.recusa.erro)}</span>
            <span className="txt-apoio text-tinta-secundaria">{resultado.recusa.comoResolver}</span>
          </div>
        )}
      </div>
    );

  const tabelaDe = (rotulo: string, lista: readonly LinhaImportadaDaApi[], vazio: string, explicacao: string) => (
    <div className="flex flex-col">
      <ListaDeLinhas
        rotulo={rotulo}
        linhas={lista}
        carregando={detalhe.isPending}
        erro={
          detalhe.isError ? (
            <EstadoVazio titulo="Não foi possível carregar as linhas" descricao={explicarFalha(detalhe.error).comoResolver} />
          ) : null
        }
        vazio={<EstadoVazio titulo={vazio} descricao={explicacao} />}
      />
      {mensagem}
    </div>
  );

  return (
    <PainelDetalhe
      titulo={lote.nomeArquivo}
      subtitulo={periodo(lote)}
      marcadores={<span className="txt-corpo text-tinta">{SITUACOES_DO_LOTE[lote.situacao] ?? lote.situacao}</span>}
      dadosChave={
        <>
          <DadoChave rotulo="Linhas válidas">{`${formatarInteiro(lote.linhasValidas)} de ${formatarInteiro(lote.linhas)}`}</DadoChave>
          <DadoChave rotulo="Importado por">{lote.criadoPor ?? '—'}</DadoChave>
          {lote.promovidoEm ? (
            <DadoChave rotulo="Promovido">{`${formatarDataHora(new Date(lote.promovidoEm))} · ${lote.promovidoPor ?? '—'}`}</DadoChave>
          ) : null}
          {lote.motivoDescarte ? <DadoChave rotulo="Motivo do descarte">{lote.motivoDescarte}</DadoChave> : null}
        </>
      }
      abas={[
        {
          id: 'erros',
          rotulo: `Com erro (${formatarInteiro(lote.linhas - lote.linhasValidas)})`,
          conteudo: tabelaDe('Linhas com erro', comErro, 'Nenhuma linha com erro', 'Todas as linhas do arquivo foram lidas. Confira as válidas e promova o lote.'),
        },
        {
          id: 'validas',
          rotulo: `Válidas (${formatarInteiro(lote.linhasValidas)})`,
          conteudo: tabelaDe('Linhas válidas', validas, 'Nenhuma linha válida', 'Corrija o arquivo pelas frases da aba Com erro, descarte este lote e importe de novo.'),
        },
        ...(emEstagio
          ? [
              {
                id: 'descartar',
                rotulo: 'Descartar',
                conteudo: (
                  <div className="flex flex-col gap-4">
                    <p className="txt-apoio text-pretty text-tinta-secundaria">
                      Descarte quando o arquivo veio errado: o lote sai da conferência, a trilha guarda o motivo,
                      e o arquivo corrigido pode entrar.
                    </p>
                    <Campo id={`${base}-motivo`} rotulo="Motivo">
                      <input
                        id={`${base}-motivo`}
                        type="text"
                        className="controle w-full"
                        placeholder="Arquivo com o mês errado"
                        value={motivo}
                        onChange={(e) => {
                          setMotivo(e.target.value);
                        }}
                      />
                    </Campo>
                    <BotaoDestrutivo
                      rotulo="Descartar lote"
                      pergunta={`Descartar o lote ${lote.nomeArquivo}?`}
                      consequencia="Nenhuma hora dele vira hora reconhecida. O mesmo arquivo pode ser importado de novo."
                      rotuloConfirmar="Descartar lote"
                      rotuloCancelar="Manter o lote"
                      aoConfirmar={aoDescartar}
                      {...(motivo.trim().length < 3 ? { motivoIndisponivel: 'Escreva o motivo do descarte antes de confirmar.' } : {})}
                    />
                    {mensagem}
                  </div>
                ),
              },
            ]
          : []),
      ]}
      acoes={
        emEstagio
          ? [
              <Botao
                key="promover"
                nivel="primario"
                onClick={aoPromover}
                disabled={promover.isPending || lote.linhasValidas === 0}
              >
                {promover.isPending ? 'Promovendo…' : 'Promover lote'}
              </Botao>,
            ]
          : []
      }
      {...(emEstagio && lote.linhasValidas === 0
        ? { notaDasAcoes: 'Sem linha válida, não há o que promover. Corrija o arquivo, descarte este lote e importe de novo.' }
        : emEstagio && lote.linhasValidas < lote.linhas
          ? { notaDasAcoes: 'Promover leva só as linhas válidas. As com erro ficam de fora até o arquivo corrigido entrar.' }
          : {})}
      aoFechar={aoFechar}
    />
  );
}
